(() => {
  if (window.__workshopQuantitySelectorInitialized) return;
  window.__workshopQuantitySelectorInitialized = true;

  const MONEY_FORMAT_REGEX = /\{\{\s*(\w+)\s*\}\}/;

  const formatWithDelimiters = (
    number,
    precision = 2,
    thousands = ",",
    decimal = "."
  ) => {
    if (Number.isNaN(number) || number == null) return "0";
    const fixedNumber = (number / 1).toFixed(precision);
    const parts = fixedNumber.split(".");
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsAmount = parts[1] ? `${decimal}${parts[1]}` : "";
    return `${dollarsAmount}${centsAmount}`;
  };

  const formatMoney = (cents, format) => {
    const formatString = format || "${{amount}}";
    const normalizedCents = Number(cents);
    const amount = Number.isFinite(normalizedCents) ? normalizedCents : 0;
    const placeholder = formatString.match(MONEY_FORMAT_REGEX)?.[1] || "amount";

    let formattedValue = "";
    switch (placeholder) {
      case "amount":
        formattedValue = formatWithDelimiters(amount / 100, 2, ",", ".");
        break;
      case "amount_no_decimals":
        formattedValue = formatWithDelimiters(amount / 100, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        formattedValue = formatWithDelimiters(amount / 100, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        formattedValue = formatWithDelimiters(amount / 100, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        formattedValue = formatWithDelimiters(amount / 100, 2, "'", ".");
        break;
      default:
        formattedValue = formatWithDelimiters(amount / 100, 2, ",", ".");
        break;
    }

    return formatString.replace(MONEY_FORMAT_REGEX, formattedValue);
  };

  const getMoneyFormat = () => {
    const currencyFormats = window.currencyFormats || {};
    const useCurrencyCode = Boolean(currencyFormats.currencyCodeEnabled);
    return useCurrencyCode
      ? currencyFormats.moneyWithCurrencyFormat
      : currencyFormats.moneyFormat;
  };

  const parseUnitPriceFromSubmit = submitButton => {
    const raw = submitButton?.dataset?.atcUnitPrice;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
  };

  const setSelectedOption = (root, optionEl) => {
    const options = Array.from(
      root.querySelectorAll(".workshop-quantity-selector__option")
    );
    for (const opt of options) {
      const isSelected = opt === optionEl;
      opt.classList.toggle("is-selected", isSelected);
      opt.setAttribute("aria-checked", isSelected ? "true" : "false");
    }
  };

  const readNumberAttr = (el, attrName, datasetName) => {
    if (!el) return null;
    const raw =
      el.getAttribute(attrName) ??
      (datasetName ? el.dataset?.[datasetName] : undefined);
    if (raw == null || raw === "") return null;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
  };

  const getQuantityConstraints = quantityInput => {
    const min = readNumberAttr(quantityInput, "min", "min") ?? 1;
    const step = readNumberAttr(quantityInput, "step") ?? 1;
    const max = readNumberAttr(quantityInput, "max", "max");
    return {
      min,
      step,
      max: max == null ? Infinity : max,
    };
  };

  const isQtyAllowed = (qty, { min, step, max }) => {
    if (!Number.isFinite(qty)) return false;
    if (qty < min) return false;
    if (qty > max) return false;
    if (!Number.isFinite(step) || step <= 0) return true;
    const delta = qty - min;
    const mod = delta % step;
    return Math.abs(mod) < 1e-9 || Math.abs(mod - step) < 1e-9;
  };

  const setOptionDisabled = (optionEl, disabled) => {
    optionEl.disabled = Boolean(disabled);
    optionEl.classList.toggle("is-disabled", Boolean(disabled));
    optionEl.setAttribute("aria-disabled", disabled ? "true" : "false");
  };

  const setAtcDisabledBySelector = (submitButton, disabled) => {
    if (!submitButton) return;
    const alreadyDisabled = submitButton.hasAttribute("disabled");

    if (disabled) {
      submitButton.setAttribute("disabled", "disabled");
      submitButton.setAttribute("aria-disabled", "true");
      submitButton.dataset.disabledByWorkshopSelector = "true";
      return;
    }

    // Only re-enable if we were the ones who disabled it
    if (submitButton.dataset.disabledByWorkshopSelector !== "true") return;
    submitButton.removeAttribute("disabled");
    submitButton.removeAttribute("aria-disabled");
    delete submitButton.dataset.disabledByWorkshopSelector;

    // If theme/Shopify logic still considers it disabled, leave it disabled
    // (Some themes toggle aria-disabled instead of disabled.)
    if (alreadyDisabled && submitButton.hasAttribute("disabled")) return;
  };

  const applyAvailability = (root, quantityInput, submitButton) => {
    const options = Array.from(
      root.querySelectorAll(".workshop-quantity-selector__option")
    );

    const soldOut = Boolean(submitButton?.hasAttribute("disabled"));
    const constraints = getQuantityConstraints(quantityInput);

    for (const opt of options) {
      const qty = Number(opt.dataset.qty || "0");
      const disabled = soldOut || !isQtyAllowed(qty, constraints);
      setOptionDisabled(opt, disabled);
    }

    const selected = root.querySelector(
      ".workshop-quantity-selector__option.is-selected"
    );
    const selectedQty = Number(selected?.dataset?.qty || "1");
    const selectedAllowed = !soldOut && isQtyAllowed(selectedQty, constraints);

    root.classList.toggle("has-unavailable-selection", !selectedAllowed);
    setAtcDisabledBySelector(submitButton, !selectedAllowed);
  };

  const syncQuantityInput = (quantityInput, qty) => {
    if (!quantityInput) return;
    quantityInput.value = `${qty}`;
    quantityInput.dispatchEvent(new Event("input", { bubbles: true }));
    quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const updateDisplayedPrices = (root, unitPriceCents) => {
    const moneyFormat = getMoneyFormat();
    const options = Array.from(
      root.querySelectorAll(".workshop-quantity-selector__option")
    );
    for (const opt of options) {
      const discount = Number(opt.dataset.discount || "0");
      const discounted = Math.round(unitPriceCents * (1 - discount));
      const priceNode = opt.querySelector("[data-discounted-unit-price]");
      const compareNode = opt.querySelector("[data-compare-unit-price]");

      if (priceNode) priceNode.textContent = formatMoney(discounted, moneyFormat);
      if (compareNode) {
        if (discount > 0) {
          compareNode.textContent = formatMoney(unitPriceCents, moneyFormat);
          compareNode.classList.remove("hidden");
        } else {
          compareNode.textContent = "";
          compareNode.classList.add("hidden");
        }
      }
    }
  };

  const initRoot = root => {
    if (root.dataset.workshopQuantitySelectorInitialized === "true") return;
    root.dataset.workshopQuantitySelectorInitialized = "true";

    const quantityInputId = root.dataset.quantityInputId;
    const submitButtonId = root.dataset.submitButtonId;
    const quantityInput = quantityInputId
      ? document.getElementById(quantityInputId)
      : null;
    const submitButton = submitButtonId
      ? document.getElementById(submitButtonId)
      : null;

    const currentQty = (() => {
      const selected = root.querySelector(
        ".workshop-quantity-selector__option.is-selected"
      );
      const qty = Number(selected?.dataset?.qty || "1");
      return Number.isFinite(qty) && qty > 0 ? qty : 1;
    })();

    syncQuantityInput(quantityInput, currentQty);

    const unitPriceCents = parseUnitPriceFromSubmit(submitButton);
    if (unitPriceCents != null) updateDisplayedPrices(root, unitPriceCents);
    applyAvailability(root, quantityInput, submitButton);

    root.addEventListener("click", evt => {
      const option = evt.target.closest(".workshop-quantity-selector__option");
      if (!option || !root.contains(option)) return;
      if (option.disabled || option.getAttribute("aria-disabled") === "true")
        return;
      const qty = Number(option.dataset.qty || "1");
      if (!Number.isFinite(qty) || qty <= 0) return;
      setSelectedOption(root, option);
      syncQuantityInput(quantityInput, qty);
      applyAvailability(root, quantityInput, submitButton);
    });

    // Price refresh on variant change for this section
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const thisSectionId = root.dataset.sectionId;
        if (!data?.sectionId || `${data.sectionId}` !== `${thisSectionId}`) return;
        const latestUnitPrice = parseUnitPriceFromSubmit(submitButton);
        if (latestUnitPrice == null) return;
        updateDisplayedPrices(root, latestUnitPrice);
        applyAvailability(root, quantityInput, submitButton);
      });
    }
  };

  const initAll = () => {
    document
      .querySelectorAll(".workshop-quantity-selector")
      .forEach(initRoot);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // When product-info swaps HTML on variant change, new nodes may be inserted.
  // Re-scan to initialize any new selectors.
  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      initAll();
    });
  }
})();

